import { useSelector } from 'react-redux';
import { BatchDetailsScreen, BatchLevelSubScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { Perspective } from '@v2/pages/common/enums';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { selectSingleBatch } from '@reducers/batch';
import { ProductionBatchStatistics } from '@interfaces/search';
import { getColorBasedOnMachineStatus, localTime, machineStatus as machineStatusConstants } from '@helpers';
import { BATCH_NOT_AVAILABLE_TEXT, BATCH_NOT_AVAILABLE_TIMEOUT_IN_MS, END_TIME_TEXT_VARIANTS, TIME_CONFIG } from '@v2/pages/machines/modules/now/components/Overview/constants';
import { Machine } from '@interfaces/machines';
import { selectSelectedItem } from '@reducers/machines';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BatchBlockProps } from '@v2/pages/machines/modules/now/components/Overview/components/BatchBlock';
import { useBatchLoaded } from '@v2/pages/machines/modules/now/components/Overview/hooks/useBatchLoaded';

interface UseManageBatchBlockParams extends Pick<BatchBlockProps, 'C'> {}

const useManageBatchBlock = ({ C }: UseManageBatchBlockParams) => {
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const batch: ProductionBatchStatistics = useSelector(selectSingleBatch);
  const machine: Machine = useSelector(selectSelectedItem);
  const batchLoaded = useBatchLoaded();

  const isBatchAvailable = machine?.machineStatus?.productionBatchId != null;

  const [isTimeoutFinished, setIsTimeoutFinished] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isBatchAvailable) {
      setIsTimeoutFinished(false);
      timeoutRef.current = setTimeout(() => {
        setIsTimeoutFinished(true);
      }, BATCH_NOT_AVAILABLE_TIMEOUT_IN_MS);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsTimeoutFinished(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isBatchAvailable]);

  const batchNotAvailableText = isTimeoutFinished
    ? BATCH_NOT_AVAILABLE_TEXT.atAll
    : BATCH_NOT_AVAILABLE_TEXT.forShortTime;

  const machineStatus = machine?.machineStatus?.status;
  const machineIsActive = machine?.machineStatus?.status === machineStatusConstants.ACTIVE;

  const colorBasedOnMachineStatus = getColorBasedOnMachineStatus(machineStatus);

  const shouldActiveBulletBeAnimated = machineIsActive;

  const batchIsRunning = batch?.isInProgress;
  const batchFinished = batch?.isClosed;
  const batchIsPaused = !batchIsRunning && !batchFinished;
  const batchLinked = batch?.links?.length > 0;

  const shouldDisplayEndTime = batch && (batchLinked || !batchIsRunning);

  const endTimeText = useMemo(() => {
    if (!shouldDisplayEndTime || batchIsRunning) {
      return END_TIME_TEXT_VARIANTS.expectedEnd;
    }

    if (batchIsPaused) {
      return END_TIME_TEXT_VARIANTS.paused;
    }

    if (batchFinished) {
      return END_TIME_TEXT_VARIANTS.end;
    }

    return '';
  }, [batchIsRunning, batchIsPaused, batchFinished, shouldDisplayEndTime]);

  const longestEndTimeText = Object.values(END_TIME_TEXT_VARIANTS).reduce((longest, text) => (text.length > longest.length ? text : longest), '');

  const startedOn = batch?.startedOn;
  const endedOn = batch?.endedOn;
  const targetDuration = batch?.scheduledTotalTimeTotalInSeconds;

  const { placeholder, format } = TIME_CONFIG;

  const startedTime = startedOn ? localTime(startedOn).format(format) : placeholder;

  const targetTime = useMemo(() => {
    if (!batchIsRunning) {
      return endedOn ? localTime(endedOn).format(format) : placeholder;
    }

    return startedOn && targetDuration ? localTime(startedOn).add(targetDuration, 's').format(format) : placeholder;
  }, [batchIsRunning, endedOn, placeholder, startedOn, targetDuration, format]);

  const onBatchBlockPress = () => {
    if (!batchLoaded) return;

    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      levelSubScreen: BatchLevelSubScreen.GENERAL,
      params: {
        perspective: Perspective.BATCH,
        batchId: batch?.productionBatchId,
        batchNumber: batch?.batchNumber,
        ncFileStatus: batch?.ncFileStatus,
      },
    });
  };

  return {
    onBatchBlockPress,
    batch,
    startedTime,
    targetTime,
    isBatchAvailable,
    batchLoaded,
    batchNotAvailableText,
    endTimeText,
    colorBasedOnMachineStatus,
    shouldActiveBulletBeAnimated,
    shouldDisplayEndTime,
    longestEndTimeText,
  };
};

export { useManageBatchBlock };
